import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  UniversalPage,
  UniversalTable,
  UniversalEmptyState,
  Card,
  Button,
  Modal,
  ModalBody,
  StatusBadge,
  type UniversalTableColumn,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { salesService, leadPaymentBadge, type LeadRow, type LeadOrder } from '@/domains/sales/services/salesService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { AddNoteForm } from '../components/AddNoteForm';
import { LeadRowActions } from '../components/LeadRowActions';

function leadDomain(o: LeadOrder | undefined): string {
  if (!o) return '—';
  if (o.fqdn_mode === 'later') return 'To be decided';
  return o.domain_name ?? '—';
}

/**
 * SalesMyLeadsPage — agent's assigned-to-me queue. Filters server-side
 * by assigned_agent_id = caller.uid (matches the customer_profile_sales_select
 * RLS predicate).
 */

export default function SalesMyLeadsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [orders, setOrders] = useState<Map<string, LeadOrder>>(new Map());
  const [loading, setLoading] = useState(true);
  const [noteFor, setNoteFor] = useState<LeadRow | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      salesService.listLeads({ assignedAgentId: user.id }),
      salesService.listLeadOrders(),
    ])
      .then(([data, orderMap]) => {
        if (!cancelled) {
          setRows(data);
          setOrders(orderMap);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  /**
   * STOCK — AR-1.3 names `listLeads()` as the example that must never take a
   * date bound, so the control says `Created`, never "Period". Same treatment
   * as SalesLeadsPage, which is the manager view of this same row set.
   */
  const table = useTableFilters<LeadRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Created',
    dateOf: (r) => r.created_at,
    searchText: (r) => [r.full_name, r.email ?? '', r.phone ?? '', r.company ?? ''].join(' '),
  });

  const columns = useMemo<UniversalTableColumn<LeadRow>[]>(
    () => [
      {
        key: 'full_name',
        header: 'Lead',
        render: (r) => (
          <Link to={`/sales/leads/${r.id}`} className="font-medium text-primary-ink hover:underline">
            {r.full_name}
          </Link>
        ),
      },
      { key: 'company', header: 'Company', render: (r) => r.company ?? '—' },
      { key: 'phone', header: 'Phone', render: (r) => r.phone ?? '—' },
      { key: 'package', header: 'Package', render: (r) => orders.get(r.id)?.plan_name ?? <span className="text-muted-foreground">—</span> },
      { key: 'domain', header: 'Domain', render: (r) => <span className="tabular-nums">{leadDomain(orders.get(r.id))}</span> },
      { key: 'payment', header: 'Payment', render: (r) => {
        const badge = leadPaymentBadge(orders.get(r.id)?.payment_state);
        return badge ? <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge> : <span className="text-muted-foreground">—</span>;
      } },
      { key: 'sales_stage', header: 'Stage', render: (r) => <StatusBadge tone={r.sales_stage === 'lead' ? 'info' : 'pending'}>{r.sales_stage}</StatusBadge> },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (r) => (
          <LeadRowActions row={r} order={orders.get(r.id)} onNote={() => setNoteFor(r)} />
        ),
      },
    ],
    [orders]
  );

  return (
    <UniversalPage
      title="My Leads"
      description="Leads assigned to you"
      actions={
        <Link to="/sales/leads/new">
          <Button variant="primary" size="sm">+ Add lead</Button>
        </Link>
      }
    >
      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      ) : rows.length === 0 ? (
        <UniversalEmptyState
          title="No leads assigned"
          description="Your manager will assign leads to you, or you can add a walk-in or inbound call directly."
          action={
            <Link to="/sales/leads/new">
              <Button variant="primary">Add lead</Button>
            </Link>
          }
        />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <PeriodFilter
              value={table.filters.period}
              from={table.filters.from}
              to={table.filters.to}
              onChange={table.filters.setPeriod}
              onStep={table.stepPeriodBy}
              presets={TABLE_PRESETS}
              variant="menu"
              steppable
              label="Created"
            />
            <SearchField
              value={table.q}
              onValueChange={table.setQ}
              pending={table.isSearching}
              placeholder="Search name, email, phone, company"
              className="ml-auto w-full max-w-xs"
            />
          </div>
          <UniversalTable
            columns={columns}
            data={table.rows}
            getRowKey={(r) => r.id}
            caption={table.caption}
          />
        </>
      )}

      {noteFor && (
        <Modal
          open
          onOpenChange={(o) => { if (!o) setNoteFor(null); }}
          title={`Add note — ${noteFor.full_name}`}
          description="Notes are visible to all sales staff who can see this lead, and are append-only."
          size="wide"
        >
          <ModalBody>
            <AddNoteForm customerProfileId={noteFor.id} autoFocus onAdded={() => setNoteFor(null)} />
          </ModalBody>
        </Modal>
      )}
    </UniversalPage>
  );
}
